import { useCallback, useMemo } from "react";
import { SpreadsheetGrid } from "@components/spreadsheet/SpreadsheetGrid";
import { ImporterSpreadsheetColumns } from "@common/helpers/financialDocument";
import { CellType } from "@components/spreadsheet/cells";
import { ColumnDef } from "@components/spreadsheet/types";
import { FinancialDocumentExtended } from "@common/types/financialDocument";
import {
    SpreadsheetValue,
    parseSpreadsheetDateValue,
} from "@common/helpers/spreadsheet";
import { sumMonetaryValues } from "@common/helpers/monetary";
import { currencyFormatter } from "@common/helpers/currency";
import { isNumber } from "@common/helpers/typeGuards";
import { HEADER_HEIGHT } from "@constants";
import { ImporterBalanceSummary } from "./ImporterBalanceSummary";
import { ImporterDocumentInfoSummary } from "./ImporterDocumentInfoSummary";
import styles from "./ImporterPage.module.scss";

const MIN_SPARE_ROWS = 10;
const DOCUMENT_INFO_SUMMARY_HEIGHT = 60;
const BALANCE_SUMMARY_HEIGHT = 80;
const TABLE_HEADER_HEIGHT = 35;
const TOTALS_ROW_HEIGHT = 40;
const UPPER_COMPONENTS_HEIGHT =
    HEADER_HEIGHT +
    DOCUMENT_INFO_SUMMARY_HEIGHT +
    BALANCE_SUMMARY_HEIGHT +
    TABLE_HEADER_HEIGHT +
    TOTALS_ROW_HEIGHT;
const ROW_HEIGHT = 40;

function hasAmount(row: Record<string, SpreadsheetValue> | undefined): boolean {
    if (!row) {
        return false;
    }
    return isNumber(row.moneyIn) || isNumber(row.moneyOut);
}

function isRowPartiallyFilled(
    row: Record<string, SpreadsheetValue> | undefined,
): boolean {
    if (!row) {
        return false;
    }

    const hasDate =
        typeof row[ImporterSpreadsheetColumns.DATE] === "string" &&
        row[ImporterSpreadsheetColumns.DATE].length > 0;
    const hasDescription =
        typeof row[ImporterSpreadsheetColumns.DESCRIPTION] === "string" &&
        row[ImporterSpreadsheetColumns.DESCRIPTION].length > 0;

    return hasDate || hasDescription || hasAmount(row);
}

function isValidDate(value: SpreadsheetValue): boolean {
    return (
        typeof value === "string" &&
        value.length > 0 &&
        parseSpreadsheetDateValue(value) !== null
    );
}

function isValidDescription(value: SpreadsheetValue): boolean {
    return typeof value === "string" && value.trim().length > 0;
}

interface Props {
    financialDocument: FinancialDocumentExtended;
    data: Array<Record<string, SpreadsheetValue>>;
    afterChange: (data: Array<Record<string, SpreadsheetValue>>) => void;
}

function transformDataForDisplay(
    data: Array<Record<string, SpreadsheetValue>>,
): Array<Record<string, SpreadsheetValue>> {
    return data.map((row) => {
        const amount = row[ImporterSpreadsheetColumns.AMOUNT];
        const numericAmount = typeof amount === "number" ? amount : null;
        return {
            ...row,
            moneyIn:
                numericAmount !== null && numericAmount >= 0
                    ? Math.abs(numericAmount)
                    : null,
            moneyOut:
                numericAmount !== null && numericAmount < 0
                    ? Math.abs(numericAmount)
                    : null,
        };
    });
}

export const ImporterDataPreviewContent: React.FC<Props> = ({
    financialDocument,
    data,
    afterChange,
}) => {
    const spareRows = useMemo(() => {
        const availableHeight = window.innerHeight - UPPER_COMPONENTS_HEIGHT;
        return Math.max(
            Math.ceil(availableHeight / ROW_HEIGHT) - data.length,
            MIN_SPARE_ROWS,
        );
    }, [data.length]);

    const displayData = useMemo(() => transformDataForDisplay(data), [data]);

    const columns: ColumnDef[] = useMemo(
        () => [
            {
                field: ImporterSpreadsheetColumns.DATE,
                label: "Date",
                cellType: CellType.DATE,
                aggregation: () => "Total",
                isValid: ({ row, value }) =>
                    !isRowPartiallyFilled(displayData[row]) ||
                    isValidDate(value),
            },
            {
                field: ImporterSpreadsheetColumns.DESCRIPTION,
                label: "Description",
                cellType: CellType.STRING,
                isValid: ({ row, value }) =>
                    !isRowPartiallyFilled(displayData[row]) ||
                    isValidDescription(value),
            },
            {
                field: "moneyIn",
                label: "Cash in",
                cellType: CellType.NUMBER,
                isMonetary: true,
                aggregation: (rows) => {
                    const sum = sumMonetaryValues(
                        rows.map(({ moneyIn }) => moneyIn ?? 0),
                    );
                    return currencyFormatter.format(sum);
                },
                isValid: ({ row }) =>
                    !isRowPartiallyFilled(displayData[row]) ||
                    hasAmount(displayData[row]),
            },
            {
                field: "moneyOut",
                label: "Cash out",
                cellType: CellType.NUMBER,
                isMonetary: true,
                aggregation: (rows) => {
                    const sum = sumMonetaryValues(
                        rows.map(({ moneyOut }) => moneyOut ?? 0),
                    );
                    return currencyFormatter.format(sum);
                },
                isValid: ({ row }) =>
                    !isRowPartiallyFilled(displayData[row]) ||
                    hasAmount(displayData[row]),
            },
        ],
        [displayData],
    );

    const handleChange = useCallback(
        (newDisplayData: Array<Record<string, SpreadsheetValue>>) => {
            const convertedData = newDisplayData.map((row, idx) => {
                const { moneyIn, moneyOut, ...rest } = row;
                const prevRow = displayData[idx];
                const prevMoneyIn =
                    typeof prevRow?.moneyIn === "number"
                        ? prevRow.moneyIn
                        : null;
                const prevMoneyOut =
                    typeof prevRow?.moneyOut === "number"
                        ? prevRow.moneyOut
                        : null;

                const moneyInValue =
                    typeof moneyIn === "number" ? moneyIn : null;
                const moneyOutValue =
                    typeof moneyOut === "number" ? moneyOut : null;

                const moneyInChanged = moneyInValue !== prevMoneyIn;
                const moneyOutChanged = moneyOutValue !== prevMoneyOut;

                let amount: SpreadsheetValue =
                    rest[ImporterSpreadsheetColumns.AMOUNT] ?? null;

                if (moneyInChanged) {
                    amount = moneyInValue;
                } else if (moneyOutChanged) {
                    if (moneyOutValue === null) {
                        amount = null;
                    } else if (moneyOutValue === 0) {
                        // ensure we're saving 0, and not -0
                        amount = 0;
                    } else {
                        amount = -moneyOutValue;
                    }
                }

                return {
                    ...rest,
                    [ImporterSpreadsheetColumns.AMOUNT]: amount,
                };
            });
            afterChange(convertedData);
        },
        [afterChange, displayData],
    );

    return (
        <>
            <ImporterDocumentInfoSummary
                financialDocument={financialDocument}
            />
            <ImporterBalanceSummary financialDocument={financialDocument} />
            <SpreadsheetGrid
                className={styles.transactionsTable}
                data={displayData}
                columns={columns}
                onChange={handleChange}
                minSpareRows={spareRows}
                noOuterBorder
                stickyHeader
                showTotalsRow
                gridTemplateColumns="120px 1fr 110px 110px"
            />
        </>
    );
};
